import useLocalStorage from 'core/hooks/useLocalStorage'
import cn from 'classnames'
import { useSettings } from 'core/hooks/useSettings'
import useModal from 'core/hooks/useModal'
import Markdown from 'marked-react'
import React, { ReactNode } from 'react'
import moment from 'moment'
import 'styles/markdown.scss'
import { useQuery } from 'react-query'
import APIService from 'api/APIService'
import Drawer from 'core/components/drawer/Drawer'
import { ChangeLogBody } from 'types/entity'
import FeatureIcon from 'core/components/icons/FeatureIcon'
import ListHeader from 'core/widgets/changelog/ListHeader'

interface ChangeLogProps {
  hide: boolean
  isTeamSpace: boolean
}

const UpdateTypeTag: Record<
  ChangeLogBody['releases'][0]['updates'][0]['type'],
  ReactNode
> = {
  new: (
    <span className="border border-dark-3 rounded-md px-2 py-1 text-sm">
      🚀 NEW FEATURE
    </span>
  ),
  fix: (
    <span className="border border-dark-3 rounded-md px-2 py-1 text-sm">
      🔨 FIX
    </span>
  ),
  update: (
    <span className="border border-dark-3 rounded-md px-2 py-1 text-sm">
      🔄 UPDATE
    </span>
  )
}

const ChangeLog = ({ hide, isTeamSpace }: ChangeLogProps) => {
  const { open, openModal, closeModal } = useModal()
  const [date, setDate] = useLocalStorage('changelog_update', '')

  const {
    data: {
      featureFlags: { ENABLE_CHANGELOG }
    }
  } = useSettings()

  const { data } = useQuery<ChangeLogBody>(
    ['changelog'],
    APIService.misc.getChangelog,
    {
      refetchOnWindowFocus: false
    }
  )

  const hasOpenChangelog = data?.last_update === date

  if (!ENABLE_CHANGELOG || !data?.last_update || hide) return null

  return (
    <>
      <div className="pl-1">
        <div
          className={cn([
            isTeamSpace ? 'text-white' : 'text-dark',
            'hover:bg-white hover:!text-primary p-2 rounded-lg cursor-pointer flex items-center justify-between'
          ])}
          onClick={() => {
            openModal()
            setDate(data?.last_update as string)
          }}
        >
          <p className="w-24 select-none text-nowrap ml-5">What's new</p>
          {hasOpenChangelog ? null : (
            <span className="bg-danger h-3 w-3 rounded-full mr-3">&nbsp;</span>
          )}
        </div>
      </div>
      <Drawer
        show={open}
        onClose={closeModal}
        title=""
        startFrom="right"
        canExpand={false}
        width="md"
        contentClassNames="pb-20 overflow-scroll h-screen"
      >
        <div className="flex flex-col gap-5">
          <div className="flex items-end px-6 py-5 gap-3 border-b border-dark-2 mb-10">
            <p className="m-0 text-3xl font-semibold">What is new?</p>
            <p className="m-0 text-dark-4 leading-6">
              Latest updates and improvements in Everlance
            </p>
            <FeatureIcon name="everlance" className="-mb-1" width={15} />
          </div>

          {data?.releases.map((release, releaseIndex) => {
            if (!release.show) return null

            return (
              <div key={releaseIndex} className="relative mb-10">
                <ListHeader>{moment(release.date).format('LL')}</ListHeader>
                {release.updates.map((update, updateIndex) => {
                  const typeTag =
                    UpdateTypeTag[update.type] ?? UpdateTypeTag.update

                  const productTags = update.products.map((p) => {
                    return (
                      <span
                        className="rounded-md px-2 py-1 text-white uppercase text-sm"
                        style={{
                          backgroundColor: p.color || '#31B59F'
                        }}
                      >
                        {p.name}
                      </span>
                    )
                  })
                  return (
                    <div key={updateIndex} className="px-6 mb-10 last:mb-0">
                      <div className="px-2">
                        <h2 className="text-2xl font-bold my-4">
                          {update.title}
                        </h2>

                        <div className="mb-5 flex items-center gap-3">
                          {typeTag}
                          {productTags}
                        </div>

                        {update.changes.map((change, changeIndex) => {
                          if (change.scope === 'admin' && !isTeamSpace)
                            return null
                          if (change.scope === 'user' && isTeamSpace)
                            return null

                          return (
                            <div
                              key={changeIndex}
                              className="markdown-text my-5 last:my-0"
                            >
                              <Markdown>
                                {change.message.replaceAll('\\n', `\n`)}
                              </Markdown>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </Drawer>
    </>
  )
}

export default ChangeLog
